import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
export const useDrawingEvents = ({ svgCanvasRef, svgCanvasInstanceRef, tool, brushWidth, handleAddWall, handleAddDoor, handleAddWindow, handleAddTextAtPosition, handleAddMeasurement, textValue, }) => {
    const theme = useTheme();
    useEffect(() => {
        const canvas = svgCanvasInstanceRef.current;
        const containerElement = svgCanvasRef.current;
        if (!canvas || !containerElement)
            return;
        // Helper function to check if canvas is ready
        const isCanvasReady = () => {
            var _a, _b;
            if (!canvas)
                return false;
            try {
                const svgRoot = (_b = (_a = canvas).getSvgRoot) === null || _b === void 0 ? void 0 : _b.call(_a);
                if (!svgRoot)
                    return false;
                // Check if SVG is in the DOM
                if (!svgRoot.ownerDocument || !svgRoot.ownerDocument.body.contains(svgRoot)) {
                    return false;
                }
                // Check if getScreenCTM would work
                const ctm = svgRoot.getScreenCTM();
                return ctm !== null;
            }
            catch (_c) {
                return false;
            }
        };
        let isDrawing = false;
        let currentPath = null;
        let points = [];
        let measureStartPoint = null;
        let measureLine = null;
        const getSVGPoint = (e) => {
            var _a, _b, _c, _d, _e, _f;
            try {
                // Check if canvas is ready first
                if (!isCanvasReady()) {
                    return null;
                }
                const clientX = "touches" in e ? (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX : e.clientX;
                const clientY = "touches" in e ? (_b = e.touches[0]) === null || _b === void 0 ? void 0 : _b.clientY : e.clientY;
                if (clientX == null || clientY == null)
                    return null;
                if (!canvas)
                    return null;
                const rootSctm = (_d = (_c = canvas).getrootSctm) === null || _d === void 0 ? void 0 : _d.call(_c);
                if (rootSctm) {
                    return {
                        x: rootSctm.a * clientX + rootSctm.c * clientY + rootSctm.e,
                        y: rootSctm.b * clientX + rootSctm.d * clientY + rootSctm.f,
                    };
                }
                const svgRoot = (_f = (_e = canvas).getSvgRoot) === null || _f === void 0 ? void 0 : _f.call(_e);
                if (!svgRoot)
                    return null;
                const pt = svgRoot.createSVGPoint();
                pt.x = clientX;
                pt.y = clientY;
                const screenCTM = svgRoot.getScreenCTM();
                if (!screenCTM) {
                    // If getScreenCTM returns null, try to get the bounding rect as fallback
                    const rect = svgRoot.getBoundingClientRect();
                    if (rect) {
                        return {
                            x: clientX - rect.left,
                            y: clientY - rect.top,
                        };
                    }
                    return null;
                }
                const svgPoint = pt.matrixTransform(screenCTM.inverse());
                return { x: svgPoint.x, y: svgPoint.y };
            }
            catch (error) {
                console.error("Error getting SVG point:", error);
                return null;
            }
        };
        const smoothPath = (points) => {
            if (points.length < 2)
                return "";
            let path = `M ${points[0].x} ${points[0].y}`;
            for (let i = 1; i < points.length; i++) {
                if (i === 1) {
                    path += ` L ${points[i].x} ${points[i].y}`;
                }
                else {
                    const prevPoint = points[i - 1];
                    const currPoint = points[i];
                    const midX = (prevPoint.x + currPoint.x) / 2;
                    const midY = (prevPoint.y + currPoint.y) / 2;
                    path += ` Q ${prevPoint.x} ${prevPoint.y} ${midX} ${midY}`;
                }
            }
            return path;
        };
        const handleMouseDown = (e) => {
            var _a, _b, _c, _d, _e;
            if (tool === "measure") {
                try {
                    // Check if canvas is ready before proceeding
                    if (!isCanvasReady()) {
                        console.warn("Canvas not ready for measure tool");
                        return;
                    }
                    const point = getSVGPoint(e);
                    if (!point)
                        return;
                    const canvas = svgCanvasInstanceRef.current;
                    if (!canvas)
                        return;
                    // Check if SVG root is available
                    const svgRoot = (_b = (_a = canvas).getSvgRoot) === null || _b === void 0 ? void 0 : _b.call(_a);
                    if (!svgRoot)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    measureStartPoint = point;
                    // Create a temporary line for visual feedback
                    const createdElement = canvas.addSVGElementsFromJson({
                        element: "line",
                        curStyles: false,
                        attr: {
                            x1: point.x,
                            y1: point.y,
                            x2: point.x,
                            y2: point.y,
                            id: canvas.getNextId(),
                            stroke: "#000000",
                            "stroke-width": 1,
                            "stroke-dasharray": "5,5",
                            opacity: 0.7,
                        },
                    });
                    if (createdElement) {
                        measureLine = (((_c = createdElement.tagName) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === "g" ? createdElement.querySelector("line") : createdElement);
                        if (measureLine) {
                            measureLine.setAttribute("style", `${measureLine.getAttribute("style") || ""}; pointer-events: none;`.trim());
                        }
                    }
                }
                catch (error) {
                    console.error("Error in measure tool mousedown:", error);
                }
                return;
            }
            if (tool !== "draw")
                return;
            const point = getSVGPoint(e);
            if (!point)
                return;
            isDrawing = true;
            points = [point];
            try {
                const createdElement = canvas.addSVGElementsFromJson({
                    element: "path",
                    curStyles: false,
                    attr: {
                        id: canvas.getNextId(),
                        d: `M ${point.x} ${point.y}`,
                        fill: "none",
                        stroke: theme.palette.primary.main,
                        "stroke-width": brushWidth.toString(),
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                    },
                });
                if (createdElement) {
                    currentPath = (((_d = createdElement.tagName) === null || _d === void 0 ? void 0 : _d.toLowerCase()) === "g" ? createdElement.querySelector("path") : createdElement);
                    if (currentPath && !((_e = currentPath.getAttribute("style")) === null || _e === void 0 ? void 0 : _e.includes("pointer-events"))) {
                        currentPath.setAttribute("style", `${currentPath.getAttribute("style") || ""}; pointer-events: auto;`.trim());
                    }
                    if (currentPath)
                        canvas.call("changed", [currentPath]);
                }
            }
            catch (error) {
                console.error("Error starting draw:", error);
            }
        };
        const handleMouseMove = (e) => {
            if (tool === "measure" && measureStartPoint && measureLine) {
                try {
                    // Check if canvas is still ready
                    if (!isCanvasReady()) {
                        return;
                    }
                    const point = getSVGPoint(e);
                    if (!point || !measureLine)
                        return;
                    measureLine.setAttribute("x2", point.x.toString());
                    measureLine.setAttribute("y2", point.y.toString());
                    canvas.call("changed", [measureLine]);
                }
                catch (error) {
                    console.error("Error in measure tool mousemove:", error);
                }
                return;
            }
            if (tool !== "draw" || !isDrawing || !currentPath)
                return;
            const point = getSVGPoint(e);
            if (!point)
                return;
            points.push(point);
            if (points.length > 3) {
                const pathData = smoothPath(points);
                currentPath.setAttribute("d", pathData);
                canvas.call("changed", [currentPath]);
            }
            else if (points.length === 2) {
                currentPath.setAttribute("d", `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`);
            }
        };
        const handleMouseUp = () => {
            var _a, _b, _c, _d, _e, _f;
            if (tool === "measure" && measureStartPoint && measureLine) {
                try {
                    const canvas = svgCanvasInstanceRef.current;
                    if (!canvas) {
                        measureStartPoint = null;
                        measureLine = null;
                        return;
                    }
                    const x2 = parseFloat(measureLine.getAttribute("x2") || "0");
                    const y2 = parseFloat(measureLine.getAttribute("y2") || "0");
                    const parentGroup = measureLine.parentElement;
                    const svgRoot = (_b = (_a = canvas).getSvgRoot) === null || _b === void 0 ? void 0 : _b.call(_a);
                    if (parentGroup && ((_c = parentGroup.tagName) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === "g") {
                        const groupChildren = Array.from(parentGroup.children);
                        if (groupChildren.length === 1 && groupChildren[0] === measureLine) {
                            if (parentGroup.parentNode) {
                                parentGroup.parentNode.removeChild(parentGroup);
                            }
                        }
                        else {
                            parentGroup.removeChild(measureLine);
                        }
                    }
                    else if (measureLine.parentNode && measureLine.parentNode !== svgRoot) {
                        measureLine.parentNode.removeChild(measureLine);
                    }
                    else if (svgRoot && svgRoot.contains(measureLine)) {
                        svgRoot.removeChild(measureLine);
                    }
                    handleAddMeasurement(measureStartPoint, { x: x2, y: y2 });
                    measureStartPoint = null;
                    measureLine = null;
                }
                catch (error) {
                    console.error("Error in measure tool mouseup:", error);
                    if (measureLine) {
                        const parentGroup = measureLine.parentElement;
                        const svgRoot = (_e = (_d = canvas).getSvgRoot) === null || _e === void 0 ? void 0 : _e.call(_d);
                        if (parentGroup && ((_f = parentGroup.tagName) === null || _f === void 0 ? void 0 : _f.toLowerCase()) === "g") {
                            const groupChildren = Array.from(parentGroup.children);
                            if (groupChildren.length === 1 && groupChildren[0] === measureLine) {
                                if (parentGroup.parentNode) {
                                    parentGroup.parentNode.removeChild(parentGroup);
                                }
                            }
                            else {
                                parentGroup.removeChild(measureLine);
                            }
                        }
                        else if (measureLine.parentNode && measureLine.parentNode !== svgRoot) {
                            measureLine.parentNode.removeChild(measureLine);
                        }
                        else if (svgRoot && svgRoot.contains(measureLine)) {
                            svgRoot.removeChild(measureLine);
                        }
                    }
                    measureStartPoint = null;
                    measureLine = null;
                }
                return;
            }
            if (tool !== "draw" || !isDrawing || !currentPath)
                return;
            isDrawing = false;
            if (points.length > 0 && currentPath) {
                currentPath.setAttribute("d", smoothPath(points));
                canvas.call("changed", [currentPath]);
                canvas.clearSelection();
            }
            currentPath = null;
            points = [];
        };
        const handleClick = (e) => {
            if (tool === "measure") {
                // Measure tool is handled by drag (mousedown/mousemove/mouseup)
                return;
            }
            if (tool === "wall" || tool === "door" || tool === "window" || tool === "text") {
                try {
                    const point = getSVGPoint(e);
                    if (!point)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    const { x, y } = point;
                    if (tool === "wall") {
                        const halfLength = 50;
                        handleAddWall({ x: x - halfLength, y }, { x: x + halfLength, y });
                    }
                    else if (tool === "door") {
                        handleAddDoor({ x, y });
                    }
                    else if (tool === "window") {
                        handleAddWindow({ x, y });
                    }
                    else if (tool === "text" && textValue.trim()) {
                        handleAddTextAtPosition({ x, y });
                    }
                }
                catch (error) {
                    console.error("Error handling click:", error);
                }
            }
        };
        containerElement.addEventListener("mousedown", handleMouseDown);
        containerElement.addEventListener("mousemove", handleMouseMove);
        containerElement.addEventListener("mouseup", handleMouseUp);
        const handleMouseLeave = () => {
            var _a, _b;
            // Clean up measure tool if mouse leaves canvas
            if (tool === "measure" && measureLine) {
                try {
                    const elementToRemove = ((_b = (_a = measureLine.parentElement) === null || _a === void 0 ? void 0 : _a.tagName) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === "g" ? measureLine.parentElement : measureLine;
                    if (elementToRemove && elementToRemove.parentNode) {
                        elementToRemove.parentNode.removeChild(elementToRemove);
                    }
                }
                catch (error) {
                    console.error("Error cleaning up measure tool on mouse leave:", error);
                }
                measureStartPoint = null;
                measureLine = null;
            }
            handleMouseUp();
        };
        containerElement.addEventListener("mouseleave", handleMouseLeave);
        containerElement.addEventListener("touchstart", handleMouseDown);
        containerElement.addEventListener("touchmove", handleMouseMove);
        containerElement.addEventListener("touchend", handleMouseUp);
        containerElement.addEventListener("click", handleClick);
        return () => {
            containerElement.removeEventListener("mousedown", handleMouseDown);
            containerElement.removeEventListener("mousemove", handleMouseMove);
            containerElement.removeEventListener("mouseup", handleMouseUp);
            containerElement.removeEventListener("mouseleave", handleMouseLeave);
            containerElement.removeEventListener("touchstart", handleMouseDown);
            containerElement.removeEventListener("touchmove", handleMouseMove);
            containerElement.removeEventListener("touchend", handleMouseUp);
            containerElement.removeEventListener("click", handleClick);
        };
    }, [
        tool,
        brushWidth,
        theme.palette.primary.main,
        svgCanvasRef,
        svgCanvasInstanceRef,
        handleAddWall,
        handleAddDoor,
        handleAddWindow,
        handleAddTextAtPosition,
        handleAddMeasurement,
        textValue,
    ]);
};
